import { forwardRef } from 'react';
import { cn } from '../../lib/utils.js';

const cardVariants = {
  default: 'bg-white dark:bg-dark-2 border border-transparent dark:border-dark-3 shadow-card hover:shadow-card-hover',
  bordered: 'bg-white border border-gray-200 hover:border-primary',
  dark: 'bg-dark-1 text-white shadow-card',
  transparent: 'bg-transparent',
};

/**
 * Card component with multiple variants
 */
export const Card = forwardRef(function Card(
  {
    children,
    variant = 'default',
    href,
    className,
    padding = true,
    rounded = true,
    as,
    ...props
  },
  ref
) {
  const baseClasses = cn(
    'block transition-all duration-400',
    cardVariants[variant],
    padding && 'p-6',
    rounded && 'rounded-lg',
    href && 'cursor-pointer',
    className
  );

  const Component = as || (href ? 'a' : 'div');

  return (
    <Component
      ref={ref}
      href={href}
      className={baseClasses}
      {...props}
    >
      {children}
    </Component>
  );
});

/**
 * Card Image component
 */
export function CardImage({ src, alt, aspectRatio = 'landscape', className, ...props }) {
  const aspectClasses = {
    square: 'aspect-square',
    portrait: 'aspect-portrait',
    landscape: 'aspect-landscape',
    wide: 'aspect-wide',
    cinema: 'aspect-cinema',
  };

  return (
    <div className={cn('img-container overflow-hidden rounded-t-lg', aspectClasses[aspectRatio], className)}>
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
        loading="lazy"
        {...props}
      />
    </div>
  );
}

/**
 * Card Body component
 */
export function CardBody({ children, className, ...props }) {
  return (
    <div className={cn('p-6', className)} {...props}>
      {children}
    </div>
  );
}

/**
 * Card Title component
 */
export function CardTitle({ children, as: Component = 'h3', className, ...props }) {
  return (
    <Component className={cn('font-heading font-bold text-xl mb-2 text-gray-900 dark:text-gray-100', className)} {...props}>
      {children}
    </Component>
  );
}

/**
 * Card Description component
 */
export function CardDescription({ children, className, ...props }) {
  return (
    <p className={cn('text-gray-600 dark:text-gray-300 text-sm', className)} {...props}>
      {children}
    </p>
  );
}

/**
 * Card Footer component
 */
export function CardFooter({ children, className, ...props }) {
  return (
    <div className={cn('px-6 py-4 border-t border-gray-100', className)} {...props}>
      {children}
    </div>
  );
}

// Attach sub-components
Card.Image = CardImage;
Card.Body = CardBody;
Card.Title = CardTitle;
Card.Description = CardDescription;
Card.Footer = CardFooter;

export default Card;
